import { createContext, useState } from 'react'

import { ICompanyProvidersResponseDTO } from '../../sdk/company/company.types'
import {
  IInvoiceInternalDataDTO,
  IPaginatedCompanyInvoiceResponse,
  IPaginatedInvoiceItemsResponse,
} from '../../sdk/invoices/invoice.types'
import { IOrderOriginPaginatedResponse } from '../../sdk/order/order.types'
import {
  IOrderInvoiceTraceabilityStatistics,
  OrderInvoiceRawStatisticsDataDTO,
} from '../../sdk/traceability/traceability.types'
import { sdk } from '../../services/sdk'

interface OriginInvoiceDataContext {
  providers: ICompanyProvidersResponseDTO[]
  invoice?: IInvoiceInternalDataDTO
  invoiceCodesData?: IPaginatedCompanyInvoiceResponse
  invoiceStatistics?: IOrderInvoiceTraceabilityStatistics
  invoiceStatisticsData?: OrderInvoiceRawStatisticsDataDTO
  invoiceItems?: IPaginatedInvoiceItemsResponse
  orders?: IOrderOriginPaginatedResponse

  loading: boolean
  loadingItems: boolean
  error: string | null

  actions: {
    getProviders: (filter?: string) => Promise<void>
    getInvoice: (providerTaxId: string, code: string) => Promise<void>
    getAllInvoices: (
      providerTaxId: string,
      page: number,
      limit?: number,
      code?: string,
    ) => Promise<void>
    getInvoiceStatistics: (invoiceId: string) => Promise<void>
    getInvoiceStatisticsData: (invoiceId: string) => Promise<void>
    getInvoiceItems: (invoiceId: string, page: number) => Promise<void>
    getOrders: (invoiceId: string, page: number) => Promise<void>
  }
}

interface OriginInvoiceProviderProps {
  children: React.ReactNode
}

export const OriginInvoiceContext = createContext(
  {} as OriginInvoiceDataContext,
)

export function OriginInvoiceProvider(props: OriginInvoiceProviderProps) {
  const [providers, setProviders] = useState<ICompanyProvidersResponseDTO[]>([])
  const [invoice, setInvoice] = useState<IInvoiceInternalDataDTO>()
  const [invoiceCodesData, setInvoiceCodesData] =
    useState<IPaginatedCompanyInvoiceResponse>()
  const [invoiceStatistics, setInvoiceStatistics] =
    useState<IOrderInvoiceTraceabilityStatistics>()
  const [invoiceStatisticsData, setInvoiceStatisticsData] =
    useState<OrderInvoiceRawStatisticsDataDTO>()
  const [invoiceItems, setInvoiceItems] =
    useState<IPaginatedInvoiceItemsResponse>()
  const [orders, setOrders] = useState<IOrderOriginPaginatedResponse>()

  const [loading, setLoading] = useState(false)
  const [loadingItems, setLoadingItems] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getProviders = async (filter?: string) => {
    const _providers = await sdk.company.getProvidersAllTiersList(
      1,
      filter,
      undefined,
      undefined,
      undefined,
      10,
    )

    const filteredProviders = _providers.data.filter(
      (provider) => provider.taxId,
    )
    setProviders(filteredProviders)
  }

  const getInvoice = async (providerTaxId: string, code: string) => {
    try {
      setError(null)
      setLoading(true)
      const _invoice = await sdk.invoices.getInvoice(code, providerTaxId)
      setInvoice(_invoice)
    } catch (err) {
      const error = err as any
      setError(error?.error?.message)
    } finally {
      setLoading(false)
    }
  }

  const getInvoiceStatistics = async (invoiceId: string) => {
    try {
      const _response = await sdk.traceability.orderInvoiceStatistics(invoiceId)
      setInvoiceStatistics(_response)
    } catch (error) {}
  }

  const getInvoiceStatisticsData = async (invoiceId: string) => {
    try {
      const _response =
        await sdk.traceability.orderInvoiceStatisticsData(invoiceId)
      setInvoiceStatisticsData(_response)
    } catch (error) {}
  }

  const getInvoiceItems = async (invoiceId: string, page: number) => {
    try {
      setLoadingItems(true)
      const _response = await sdk.invoices.invoiceItems(invoiceId, page)
      setInvoiceItems(_response)
    } catch (err) {
      const error = err as any
      setError(error?.error?.message)
    } finally {
      setLoadingItems(false)
    }
  }

  const getAllInvoices = async (
    providerTaxId: string,
    page: number,
    limit?: number,
    code?: string,
  ) => {
    try {
      const _response = await sdk.invoices.getAllInvoices(
        providerTaxId,
        page,
        limit,
        code,
      )
      setInvoiceCodesData(_response)
    } catch (error) {
      console.log(error)
    }
  }

  const getOrders = async (invoiceId: string, page: number) => {
    try {
      setLoadingItems(true)
      const _response = await sdk.order.origins(invoiceId, page)
      setOrders(_response)
    } catch (err) {
    } finally {
      setLoadingItems(false)
    }
  }

  return (
    <OriginInvoiceContext.Provider
      value={{
        providers,
        invoice,
        invoiceCodesData,
        invoiceStatistics,
        invoiceStatisticsData,
        invoiceItems,
        orders,

        loading,
        loadingItems,
        error,

        actions: {
          getProviders,
          getInvoice,
          getInvoiceStatistics,
          getInvoiceStatisticsData,
          getInvoiceItems,
          getAllInvoices,
          getOrders,
        },
      }}
    >
      {props.children}
    </OriginInvoiceContext.Provider>
  )
}
